import {
    type ReactNode,
    useState
} from "react";
import {
    ComponentHeader,
    AnatomySection,
    ExampleSection,
    getSectionToc,
    PropsTable
} from "./shared";
import {
    type NCoreUIKitIcon,
    DirectionProvider,
    NCoreUIKitTheme,
    Button,
    Switch,
    Text,
    Icon
} from "ncore-ui-kit";
import {
    useDocsI18n
} from "../../i18n";
import type {
    DocsTranslate
} from "../../anatomy/types";
import {
    ArrowRight as ArrowRightIcon,
    Settings as SettingsIcon,
    Download as DownloadIcon,
    House as HouseIcon,
    Heart as HeartIcon,
    Bell as BellIcon
} from "lucide-react-native";
import {
    BulletList,
    Paragraph,
    CodeBlock,
    Article,
    Preview,
    Section
} from "../../components/content";

const NAME = "Icon";

const USAGE_CODE = `import {
    Bell as BellIcon
} from "lucide-react-native";

<Icon
    icon={BellIcon}
    size="large"
/>

<Icon
    accessibilityLabel="Notifications"
    color="emphasized"
    icon={BellIcon}
/>`;

const CONTEXT_CODE = `const ExportIcon: NCoreUIKitIcon = ({
    customColor,
    color,
    size
}) => <Icon
    customColor={customColor}
    icon={DownloadIcon}
    color={color}
    size={size}
/>;

<Button
    onPress={handleExport}
    icon={ExportIcon}
    title="Export"
/>`;

const OVERVIEW_ICONS = [
    BellIcon,
    HouseIcon,
    HeartIcon,
    SettingsIcon
];

const SIZES = [
    "small",
    "medium",
    "large"
] as const;

const COLORS = [
    "high",
    "mid",
    "low",
    "disabled",
    "emphasized",
    "danger",
    "success",
    "warning",
    "info"
] as const;

const BUTTON_VARIANTS = [
    "filled",
    "tonal",
    "outline"
] as const;

const CELL_STYLE = {
    flexDirection: "column",
    alignItems: "center",
    display: "flex",
    gap: 8
} as const;

const SLOT_STYLE = {
    justifyContent: "center",
    alignItems: "center",
    display: "flex",
    height: 32
} as const;

const DIRECTION_STYLE = {
    justifyContent: "center",
    flexDirection: "row",
    gap: 32
} as const;

const SampleCell = ({
    children,
    label
}: {
    children: ReactNode;
    label: string;
}) => <div
    style={CELL_STYLE}
>
    <div
        style={SLOT_STYLE}
    >
        {children}
    </div>
    <Text
        variant="bodySmallSize"
        color="low"
    >
        {label}
    </Text>
</div>;

const ExportIcon: NCoreUIKitIcon = ({
    customColor,
    color,
    size
}) => <Icon
    customColor={customColor}
    icon={DownloadIcon}
    color={color}
    size={size}
/>;

export const getToc = (t: DocsTranslate) => getSectionToc([
    {
        title: t("section.overview"),
        id: "overview"
    },
    {
        title: t("section.sizes"),
        id: "sizes"
    },
    {
        title: t("iconPage.colorsTitle"),
        id: "colors"
    },
    {
        title: t("iconPage.directionTitle"),
        id: "direction"
    },
    {
        title: t("iconPage.contextTitle"),
        id: "components"
    },
    {
        title: t("section.accessibility"),
        id: "accessibility"
    },
    {
        title: t("section.props"),
        id: "props"
    }
], NAME, t);

const IconPage = () => {
    const {
        t
    } = useDocsI18n();

    const {
        iconSizes
    } = NCoreUIKitTheme.useContext();

    const [
        isRtl,
        setIsRtl
    ] = useState(false);

    return <Article>
        <ComponentHeader
            lead={t("iconPage.lead")}
            name={NAME}
        />
        <Section
            title={t("section.overview")}
            id="overview"
        >
            <Paragraph>
                {t("iconPage.overviewText")}
            </Paragraph>
            <Preview>
                {
                    OVERVIEW_ICONS.map((icon, index) => <Icon
                        size="large"
                        icon={icon}
                        key={index}
                    />)
                }
            </Preview>
            <CodeBlock
                code={USAGE_CODE}
                language="tsx"
            />
        </Section>
        <AnatomySection
            name={NAME}
        />
        <Section
            title={t("section.sizes")}
            id="sizes"
        >
            <Paragraph>
                {t("iconPage.sizesText")}
            </Paragraph>
            <Preview>
                {
                    SIZES.map((size) => <SampleCell
                        label={`${size} · ${iconSizes[size]}`}
                        key={size}
                    >
                        <Icon
                            icon={BellIcon}
                            size={size}
                        />
                    </SampleCell>)
                }
                <SampleCell
                    label="32"
                >
                    <Icon
                        icon={BellIcon}
                        size={32}
                    />
                </SampleCell>
            </Preview>
        </Section>
        <Section
            title={t("iconPage.colorsTitle")}
            id="colors"
        >
            <Paragraph>
                {t("iconPage.colorsText")}
            </Paragraph>
            <Preview>
                {
                    COLORS.map((color) => <SampleCell
                        label={color}
                        key={color}
                    >
                        <Icon
                            icon={BellIcon}
                            color={color}
                            size="large"
                        />
                    </SampleCell>)
                }
                <SampleCell
                    label="customColor"
                >
                    <Icon
                        customColor="english"
                        icon={BellIcon}
                        size="large"
                    />
                </SampleCell>
            </Preview>
        </Section>
        <Section
            title={t("iconPage.directionTitle")}
            id="direction"
        >
            <Paragraph>
                {t("iconPage.directionText")}
            </Paragraph>
            <Preview
                caption={t("iconPage.directionCaption")}
                isStacked={true}
            >
                <Switch
                    title={t("iconPage.directionSwitch")}
                    onCheckedChange={setIsRtl}
                    isChecked={isRtl}
                />
                <DirectionProvider
                    direction={isRtl ? "rtl" : "ltr"}
                    style={DIRECTION_STYLE}
                >
                    <SampleCell
                        label="ArrowRight"
                    >
                        <Icon
                            icon={ArrowRightIcon}
                            size="large"
                        />
                    </SampleCell>
                    <SampleCell
                        label="Bell"
                    >
                        <Icon
                            icon={BellIcon}
                            size="large"
                        />
                    </SampleCell>
                    <SampleCell
                        label="isMirrored={false}"
                    >
                        <Icon
                            icon={ArrowRightIcon}
                            isMirrored={false}
                            size="large"
                        />
                    </SampleCell>
                </DirectionProvider>
            </Preview>
        </Section>
        <Section
            title={t("iconPage.contextTitle")}
            id="components"
        >
            <Paragraph>
                {t("iconPage.contextText")}
            </Paragraph>
            <Preview>
                {
                    BUTTON_VARIANTS.map((variant) => <Button
                        title={t("iconPage.export")}
                        onPress={() => undefined}
                        variant={variant}
                        icon={ExportIcon}
                        key={variant}
                    />)
                }
                <Button
                    title={t("iconPage.export")}
                    onPress={() => undefined}
                    isDisabled={true}
                    icon={ExportIcon}
                />
            </Preview>
            <CodeBlock
                code={CONTEXT_CODE}
                language="tsx"
            />
        </Section>
        <Section
            title={t("section.accessibility")}
            id="accessibility"
        >
            <BulletList
                items={[
                    t("iconPage.a11yDecorative"),
                    t("iconPage.a11yLabel"),
                    t("iconPage.a11yControls"),
                    t("iconPage.a11yFocus")
                ]}
            />
        </Section>
        <Section
            title={t("section.props")}
            id="props"
        >
            <PropsTable
                rows={[
                    {
                        description: t("iconPage.propIcon"),
                        type: "LucideIcon",
                        name: "icon"
                    },
                    {
                        type: "\"small\" | \"medium\" | \"large\" | number",
                        description: t("iconPage.propSize"),
                        defaultValue: "\"medium\"",
                        name: "size"
                    },
                    {
                        description: t("iconPage.propColor"),
                        type: "keyof IconContentColors",
                        defaultValue: "\"mid\"",
                        name: "color"
                    },
                    {
                        description: t("iconPage.propCustomColor"),
                        type: "keyof ProjectColorPalette | string",
                        name: "customColor"
                    },
                    {
                        description: t("iconPage.propIsMirrored"),
                        name: "isMirrored",
                        type: "boolean"
                    },
                    {
                        description: t("iconPage.propAccessibilityLabel"),
                        name: "accessibilityLabel",
                        type: "string"
                    },
                    {
                        type: "{ themeKey, paletteKey, sharpness, gapPropagation }",
                        description: t("iconPage.propCustomTheme"),
                        name: "customTheme"
                    }
                ]}
            />
            <Paragraph
                isMuted={true}
            >
                {t("iconPage.propsNote")}
            </Paragraph>
        </Section>
        <ExampleSection
            name={NAME}
        />
    </Article>;
};
export default IconPage;
